import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CardDetailScreen.css';

const CLOSE_MS = 320; // синхронізовано з анімацією закриття в CardDetailScreen.css

// Екран опису картки (напр. «Дитяча картка») — виїжджає справа поверх
// «Відкрити картку». Як у monobank: шапка непрозора, вміст ховається під
// неї; обкладинка трохи відстає (паралакс) і легенько гойдається; великий
// заголовок їде вгору, на підході до шапки зменшується (23 → 19px) і
// прилипає в ній по центру.
const TITLE_SIZE = 23;
const BAR_TITLE_SIZE = 19;
const SHRINK_DISTANCE = 50; // за скільки px до шапки заголовок починає зменшуватись
const HERO_PARALLAX = 0.15;
const SNAP_IDLE_MS = 60; // для колеса/трекпада: скрол завершено, якщо подій немає стільки мс
const SNAP_MS = 260; // тривалість доводки
// onCta — тап по кнопці «Хочу … картку»: async, true — екран закрито ззовні
// (разом з «Відкрити картку»); opened — картку цього типу вже відкрито
export default function CardDetailScreen({ detail, onClose, onCta, opened = false }) {
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const heroRef = useRef(null);
  const titleSpaceRef = useRef(null);
  const titleRef = useRef(null);
  const barRef = useRef(null);
  const rafRef = useRef(0);
  // Доводка: між обкладинкою і прилиплим заголовком сторінка не зупиняється —
  // після скролу вниз докручується до заголовка, вгору — до самого верху
  const snapRef = useRef({ touching: false, lastTop: 0, dir: 0, timer: 0, snapping: false, anim: 0 });

  // до першого кадру — щоб заголовок одразу стояв на місці
  useLayoutEffect(() => {
    layout();
    const sn = snapRef.current;
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(sn.timer);
      cancelAnimationFrame(sn.anim);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onClose, CLOSE_MS);
  }

  async function handleCta() {
    if (busy || closing || !onCta) return;
    setBusy(true);
    const done = await onCta();
    if (done) close();
    else setBusy(false);
  }

  // Позиції рахуємо від scrollTop і пишемо прямо в стилі — без ре-рендерів
  function layout() {
    const scroll = scrollRef.current;
    const space = titleSpaceRef.current;
    const title = titleRef.current;
    const bar = barRef.current;
    if (!scroll || !space || !title || !bar) return;
    const st = scroll.scrollTop;
    // центр заголовка в шапці: safe-area + 25 (висота шапки = safe + 54)
    const stickCenter = bar.offsetHeight - 54 + 25.35;
    const naturalCenter = space.offsetTop - st + TITLE_SIZE / 2;
    const center = Math.max(stickCenter, naturalCenter);
    const t = Math.max(0, Math.min(1, (naturalCenter - stickCenter) / SHRINK_DISTANCE));
    const scale = BAR_TITLE_SIZE / TITLE_SIZE + (1 - BAR_TITLE_SIZE / TITLE_SIZE) * t;
    title.style.transform = `translate3d(0, ${center - TITLE_SIZE / 2}px, 0) scale(${scale})`;
    bar.classList.toggle('stuck', naturalCenter <= stickCenter + 0.5);
    if (heroRef.current) {
      heroRef.current.style.transform = `translate3d(0, ${Math.max(0, st) * HERO_PARALLAX}px, 0)`;
    }
  }

  // scrollTop, на якому заголовок якраз прилипає до шапки
  function titleSnapTop() {
    const space = titleSpaceRef.current;
    const bar = barRef.current;
    if (!space || !bar) return 0;
    const stickCenter = bar.offsetHeight - 54 + 25.35;
    return Math.max(0, space.offsetTop + TITLE_SIZE / 2 - stickCenter);
  }

  // Доводимо своєю анімацією (швидше за scrollTo smooth). Інерцію iOS
  // гасимо, на мить вимкнувши overflow, — інакше вона сперечалася б з доводкою.
  function maybeSnap() {
    const scroll = scrollRef.current;
    const sn = snapRef.current;
    if (!scroll || sn.touching || sn.snapping) return;
    const from = scroll.scrollTop;
    const snapTop = titleSnapTop();
    if (from <= 0.5 || from >= snapTop - 0.5) return;
    const to = sn.dir >= 0 ? snapTop : 0;
    sn.snapping = true;
    scroll.style.overflowY = 'hidden';
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / SNAP_MS);
      const e = 1 - Math.pow(1 - t, 3);
      scroll.scrollTop = from + (to - from) * e;
      layout();
      if (t < 1) {
        sn.anim = requestAnimationFrame(tick);
      } else {
        scroll.style.overflowY = '';
        sn.snapping = false;
        sn.lastTop = scroll.scrollTop;
      }
    };
    sn.anim = requestAnimationFrame(tick);
  }

  function handleScroll() {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(layout);
    const scroll = scrollRef.current;
    const sn = snapRef.current;
    if (!scroll) return;
    const st = scroll.scrollTop;
    // напрямок запам'ятовуємо лише від руху користувача, а не від доводки
    if (!sn.snapping && st !== sn.lastTop) sn.dir = st > sn.lastTop ? 1 : -1;
    sn.lastTop = st;
    clearTimeout(sn.timer);
    sn.timer = setTimeout(maybeSnap, SNAP_IDLE_MS);
  }

  function handleTouchStart() {
    const sn = snapRef.current;
    sn.touching = true;
    clearTimeout(sn.timer);
    // торкнулися під час доводки — зупиняємо її, керує палець
    if (sn.snapping) {
      cancelAnimationFrame(sn.anim);
      sn.snapping = false;
      if (scrollRef.current) scrollRef.current.style.overflowY = '';
    }
  }

  // Доводимо одразу, як палець відпущено, — не чекаючи кінця інерції
  function handleTouchEnd() {
    const sn = snapRef.current;
    sn.touching = false;
    clearTimeout(sn.timer);
    maybeSnap();
  }

  return createPortal((
    <div className={`cds-root ${closing ? 'closing' : ''}`}>
      <div className="cds-sheet">
        <div
          className="cds-scroll"
          ref={scrollRef}
          onScroll={handleScroll}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <div className="cds-hero-wrap" ref={heroRef}>
            <img className="cds-hero" src={detail.hero} alt="" />
          </div>
          {/* місце під заголовок; сам заголовок — окремий шар над шапкою */}
          <div className="cds-title-space" ref={titleSpaceRef} />

          <div className="cds-info">
            <p className="cds-info-title">{detail.info.title}</p>
            <p className="cds-info-text">{detail.info.text}</p>
          </div>

          {detail.sections.map((sec) => (
            <div className={`cds-section ${sec.emoji || sec.emojiText ? 'with-emoji' : ''}`} key={sec.title}>
              {sec.emoji && <img className={`cds-emoji ${sec.emojiClass || ''}`} src={sec.emoji} alt="" />}
              {sec.emojiText && <span className="cds-emoji cds-emoji-text">{sec.emojiText}</span>}
              <p className="cds-section-title">{sec.title}</p>
              {sec.text && <p className="cds-section-text">{sec.text}</p>}
              {sec.items && (
                <div className="cds-items">
                  {sec.items.map((it) => (
                    <div className="cds-item" key={it.text}>
                      <img className="cds-item-icon" src={it.icon} alt="" />
                      <p className="cds-item-text">{it.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {detail.footnote && <p className="cds-footnote">{detail.footnote}</p>}
        </div>

        <div className="cds-bar" ref={barRef} />
        <h1 className="cds-title" ref={titleRef}>{detail.title}</h1>
        <button type="button" className="cds-close" onClick={close} aria-label="Закрити">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4l16 16M20 4L4 20" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <button type="button" className={`cds-cta ${busy ? 'busy' : ''}`} onClick={handleCta}>
          <span className="cds-cta-title">{opened ? 'Картку вже відкрито' : detail.cta.title}</span>
          <span className="cds-cta-sub">{opened ? 'Перейти до картки' : detail.cta.sub}</span>
        </button>
      </div>
    </div>
  ), document.body);
}
